import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Spinner } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import PropertySelectComponent from "./PropertySelectComponent";
import RoomSelectComponent from "./RoomSelectComponent";
import OtaSelectComponent from "./OtaSelectComponent";
import AgentSelectComponent from "./AgentSelectComponent";
import BasedOnComponent from "./BasedOnComponent";
import DateRangePickerComponent from "./DateRangePickerComponentV2";
import ReportTypeComponent from "./ReportTypeComponent";
import { schemaMap, defaultValuesMap } from "../../schema/FilterSchema";
import { useAuth } from "../../../../auth/context/AuthProvider";
import { format } from "date-fns";

interface HeaderFilterProps {
    setReportType: (type: "country_report" | "daily_report" | "sales_report" | "report_sales_monthly" | "report_reservation" | "report_agent") => void;
    setFilters: (filters: any) => void;
    isLoading?: boolean;
}

const HeaderFilter: React.FC<HeaderFilterProps> = ({ setReportType, setFilters, isLoading }) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const activePropertyId = user?.active_property_id;

    const [reportType, internalSetReportType] = useState<keyof typeof schemaMap>("report_agent");

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
        watch,
        getValues,
    } = useForm({
        resolver: zodResolver(schemaMap[reportType]),
        defaultValues: defaultValuesMap[reportType],
        shouldUnregister: false,
    });

    // Track the last submitted form data to detect changes
    const [appliedFilters, setAppliedFilters] = useState<any>(null);

    // Track whether a fetch has completed at least once
    const [wasLoading, setWasLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    useEffect(() => {
        if (isLoading) {
            setWasLoading(true);
        } else if (wasLoading) {
            setHasFetched(true);
            setWasLoading(false);
        }
    }, [isLoading, wasLoading]);

    // Compare current form values with last submitted values to detect changes
    const currentFormValues = watch();
    const isFormChanged = appliedFilters
        ? JSON.stringify(currentFormValues) !== JSON.stringify(appliedFilters)
        : false;

    // Determine which button to show
    const showRefreshButton = hasFetched && !isFormChanged;

    useEffect(() => {
        console.log("🔄 Resetting form values...");
        reset((prevValues: any) => ({
            ...defaultValuesMap[reportType],
            filter: {
                ...prevValues.filter, // ✅ Preserve previous selections
                base_on: (defaultValuesMap[reportType].filter && "base_on" in defaultValuesMap[reportType].filter) ? defaultValuesMap[reportType].filter.base_on : undefined,
                property_id: activePropertyId || "",
            },
        }));
    }, [reportType, reset, activePropertyId]);


    const onSubmit = (data: any) => {
        console.log("✅ Form Submitted Successfully:", JSON.stringify(data, null, 2));

        // If the refresh button is visible and clicked, force React Query to refetch
        if (showRefreshButton) {
            queryClient.invalidateQueries({ queryKey: ['report'] });
        }

        // Reset hasFetched so it can track the new fetch cycle
        setHasFetched(false);

        // Store submitted data for form change comparison, using getValues() so it structurally matches watch()
        setAppliedFilters(getValues());

        if (data.report_type === "report_sales_monthly") {
            console.log("📅 Adjusting dates for report_sales_monthly...");

            const now = new Date();

            // Get the first day of the current month
            const end_date = format(new Date(now.getFullYear(), now.getMonth(), 1), "yyyy-MM-dd");

            // Get the first day of the same month last year
            const start_date = format(new Date(now.getFullYear() - 1, now.getMonth(), 1), "yyyy-MM-dd");

            const new_data = { ...data, filter: { ...data.filter, start_date, end_date } };

            console.log("📅 Adjusted dates:", new_data);

            // Update filters with adjusted dates
            setFilters(new_data);
        } else {
            setFilters(data);
        }

        console.log("📊 Showing report type:", data);

        setReportType(data.report_type);
    };

    const onError = (errors: any) => {
        console.error("❌ Validation Error:", JSON.stringify(errors, null, 2));
    };

    return (
        <form onSubmit={handleSubmit(onSubmit, onError)}>
            <div className="d-flex flex-column flex-md-row flex-wrap gap-3 justify-content-start">
                <div className="flex-fill">
                    <PropertySelectComponent setValue={setValue} />
                </div>

                {["country_report", "sales_report", "daily_report", "report_sales_monthly", "report_reservation", "report_agent", "room_report"].includes(reportType) && (
                    <div className="flex-fill">
                        <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                    </div>
                )}

                {reportType === "daily_report" && (
                    <div className="flex-fill">
                        <OtaSelectComponent control={control} errors={errors} setValue={setValue} />
                    </div>
                )}

                {reportType === "daily_report" && (
                    <div className="flex-fill">
                        <AgentSelectComponent control={control} errors={errors} setValue={setValue} />
                    </div>
                )}

                {["country_report", "sales_report", "report_sales_monthly", "report_agent", "room_report", "report_reservation"].includes(reportType as string) && (
                    <div className="flex-fill">
                        <BasedOnComponent
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            reportType={reportType as
                                | "country_report"
                                | "sales_report"
                                | "report_sales_monthly"
                                | "report_agent"
                                | "room_report"
                                | "report_reservation"}
                        />
                    </div>
                )}

                {["country_report", "sales_report", "daily_report", "report_agent", "room_report"].includes(reportType) && (
                    <div className="flex-fill">
                        <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                    </div>
                )}

                <div className="flex-fill">
                    <ReportTypeComponent control={control} setReportType={internalSetReportType} />
                </div>
            </div>

            <div className="mt-3">
                {showRefreshButton ? (
                    <Button variant="info" type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <><Spinner animation="border" size="sm" className="me-2" />Refreshing...</>
                        ) : (
                            <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Report</>
                        )}
                    </Button>
                ) : (
                    <Button variant="warning" type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                        ) : (
                            'Show Report'
                        )}
                    </Button>
                )}
            </div>
        </form>
    );
};

export default HeaderFilter;